/** Pointer travel (px) before a press on a link is treated as a drag instead of a click. */
const DRAG_THRESHOLD = 5;
/**
 * Product top navigation: a horizontal strip of product links.
 * Touch and trackpad users scroll natively; mouse users can also press and drag the strip
 * sideways when the items overflow the available width.
 */
export const ProductTopNavigation = (element) => {
    const trackElement = element.querySelector("[data-product-top-nav-track]");
    if (!trackElement) {
        return undefined;
    }
    const track = trackElement;
    let pressed = false;
    let dragging = false;
    let suppressClick = false;
    let startX = 0;
    let startScrollLeft = 0;
    const isOverflowing = () => track.scrollWidth > track.clientWidth + 1;
    const syncDraggable = () => {
        track.dataset.draggable = isOverflowing() ? "true" : "false";
    };
    const centerCurrentItem = () => {
        const current = track.querySelector("[aria-current='page']");
        if (!current || !isOverflowing()) {
            return;
        }
        const trackRect = track.getBoundingClientRect();
        const currentRect = current.getBoundingClientRect();
        const offset = currentRect.left - trackRect.left - (trackRect.width - currentRect.width) / 2;
        track.scrollLeft = Math.max(0, track.scrollLeft + offset);
    };
    const stopDragging = () => {
        if (!pressed) {
            return;
        }
        pressed = false;
        suppressClick = dragging;
        dragging = false;
        delete track.dataset.dragging;
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", stopDragging);
        window.removeEventListener("pointercancel", stopDragging);
    };
    function onPointerMove(event) {
        const distance = event.clientX - startX;
        if (!dragging && Math.abs(distance) < DRAG_THRESHOLD) {
            return;
        }
        dragging = true;
        track.dataset.dragging = "true";
        track.scrollLeft = startScrollLeft - distance;
    }
    const onPointerDown = (event) => {
        // Touch and pen keep native scrolling; only the mouse needs emulated dragging.
        if (event.pointerType !== "mouse" || event.button !== 0 || !isOverflowing()) {
            return;
        }
        pressed = true;
        dragging = false;
        suppressClick = false;
        startX = event.clientX;
        startScrollLeft = track.scrollLeft;
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", stopDragging);
        window.addEventListener("pointercancel", stopDragging);
    };
    // A drag that ends over a link must not navigate.
    const onClickCapture = (event) => {
        if (!suppressClick) {
            return;
        }
        suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
    };
    track.addEventListener("pointerdown", onPointerDown);
    track.addEventListener("click", onClickCapture, true);
    syncDraggable();
    centerCurrentItem();
    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(syncDraggable);
        resizeObserver.observe(track);
    }
    else {
        window.addEventListener("resize", syncDraggable);
    }
    return () => {
        stopDragging();
        track.removeEventListener("pointerdown", onPointerDown);
        track.removeEventListener("click", onClickCapture, true);
        resizeObserver?.disconnect();
        window.removeEventListener("resize", syncDraggable);
        delete track.dataset.draggable;
    };
};
